import { useMutation, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'

export interface InvoicePdfBlobVariables {
  dealId: string
  invoiceId: string
  /**
   * DOM element holding the rendered preview. Required for the mock path so
   * jspdf+html2canvas can rasterise the on-screen overlay; the real backend
   * branch ignores it and streams the PDF directly.
   */
  source: HTMLElement | null
}

const useMockPdf = process.env.REACT_APP_USE_MOCK_DEALS === 'true'

// Loaded lazily via <script> tags. Webpack 4 in CRA 3.4.1 either merges
// `import('html2canvas')` into a vendor chunk (~6 MB upfront) or chokes on
// transitive class-field syntax in canvg/fast-png. Side-stepping the bundler
// by injecting the pre-built UMD scripts keeps the libs out of the initial
// payload and avoids polyfill issues entirely.
const HTML2CANVAS_SRC = '/static/vendor/html2canvas.min.js'
const JSPDF_SRC = '/static/vendor/jspdf.umd.min.js'

interface JsPDFLike {
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } }
  addImage: (
    image: string,
    format: string,
    x: number,
    y: number,
    width: number,
    height: number,
    alias?: string,
    compression?: string,
  ) => void
  addPage: () => void
  output: (kind: string) => Blob
}
type JsPDFCtor = new (options: { unit: string; format: string; orientation: string }) => JsPDFLike
type Html2CanvasFn = (
  el: HTMLElement,
  options: { backgroundColor: string; scale: number; useCORS?: boolean },
) => Promise<HTMLCanvasElement>

declare global {
  interface Window {
    jspdf?: { jsPDF?: JsPDFCtor }
    html2canvas?: Html2CanvasFn
  }
}

const scriptCache = new Map<string, Promise<void>>()

function loadScript(src: string): Promise<void> {
  if (scriptCache.has(src)) return scriptCache.get(src) as Promise<void>
  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdf-src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve()
      else {
        existing.addEventListener('load', () => resolve(), { once: true })
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
          once: true,
        })
      }
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.dataset.pdfSrc = src
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true'
      resolve()
    })
    script.addEventListener('error', () => {
      script.remove()
      scriptCache.delete(src)
      reject(new Error(`Failed to load ${src}`))
    })
    document.head.appendChild(script)
  })
  scriptCache.set(src, promise)
  return promise
}

async function buildBlobFromDom(element: HTMLElement): Promise<Blob> {
  await Promise.all([loadScript(HTML2CANVAS_SRC), loadScript(JSPDF_SRC)])
  const html2canvas = window.html2canvas
  const jsPDF = window.jspdf?.jsPDF
  if (!html2canvas || !jsPDF) {
    throw new Error('PDF libraries failed to register on window')
  }
  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: window.devicePixelRatio > 1 ? 2 : 1.5,
    useCORS: true,
  })
  const imageData = canvas.toDataURL('image/png')
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const ratio = canvas.height / canvas.width
  const renderedHeight = pageWidth * ratio
  if (renderedHeight <= pageHeight) {
    pdf.addImage(imageData, 'PNG', 0, 0, pageWidth, renderedHeight, undefined, 'FAST')
  } else {
    let consumed = 0
    while (consumed < renderedHeight) {
      pdf.addImage(
        imageData,
        'PNG',
        0,
        -consumed,
        pageWidth,
        renderedHeight,
        undefined,
        'FAST',
      )
      consumed += pageHeight
      if (consumed < renderedHeight) pdf.addPage()
    }
  }
  return pdf.output('blob')
}

export function useInvoicePdfBlob(): UseMutationResult<Blob, unknown, InvoicePdfBlobVariables> {
  const handleError = useApiErrorHandler()

  return useMutation<Blob, unknown, InvoicePdfBlobVariables>(
    async ({ dealId, invoiceId, source }) => {
      if (useMockPdf) {
        if (!source) {
          throw new Error('Mock PDF generator needs a DOM source element')
        }
        return buildBlobFromDom(source)
      }
      return dealsAPI.getInvoicePdfBlob(dealId, invoiceId)
    },
    {
      onError: (error) => handleError(error),
    },
  )
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Defer revoke so Safari has time to start the download stream.
  setTimeout(() => URL.revokeObjectURL(url), 1500)
}
